import type { NoteBody } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

type Span = Readonly<{
  text: string;
  bold?: boolean;
}>;
type Paragraph = Readonly<{
  kind: 'p';
  spans: readonly Span[];
}>;
type BulletList = Readonly<{
  kind: 'ul';
  items: readonly (readonly Span[])[];
}>;
type Block = Paragraph | BulletList;

// Lexical text format bitmask: 1 = bold.
const FORMAT_BOLD = 1;

const p = (...spans: Span[]): Paragraph => ({ kind: 'p', spans });
const ul = (...items: Span[][]): BulletList => ({ kind: 'ul', items });

const textNode = ({ text, bold }: Span) => ({
  type: 'text',
  text,
  detail: 0,
  format: bold ? FORMAT_BOLD : 0,
  mode: 'normal',
  style: '',
  version: 1,
});

const paragraphNode = (spans: readonly Span[]) => ({
  type: 'paragraph',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 1,
  textFormat: 0,
  textStyle: '',
  children: spans.map(textNode),
});

const listItemNode = (spans: readonly Span[], value: number) => ({
  type: 'listitem',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 1,
  value,
  children: spans.map(textNode),
});

const listNode = (items: readonly (readonly Span[])[]) => ({
  type: 'list',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 1,
  listType: 'bullet',
  tag: 'ul',
  start: 1,
  children: items.map((spans, i) => listItemNode(spans, i + 1)),
});

const buildBody = (blocks: readonly Block[]): NoteBody => ({
  root: {
    type: 'root',
    direction: 'ltr',
    format: '',
    indent: 0,
    version: 1,
    children: blocks.map((block) =>
      block.kind === 'p' ? paragraphNode(block.spans) : listNode(block.items),
    ),
  },
});

export type CoachNoteTemplate = Readonly<{
  id: string;
  label: string;
  body: NoteBody;
}>;

export const COACH_NOTE_TEMPLATES: readonly CoachNoteTemplate[] = [
  {
    id: 'monthly-coaching-agenda',
    label: 'Monthly coaching agenda',
    body: buildBody([
      p({ text: 'Monthly Coaching Agenda', bold: true }),
      p(),
      p({ text: '1. Reflect - Start with a recent win', bold: true }),
      p(),
      p({ text: 'Surface a win', bold: true }),
      ul(
        [{ text: 'What’s something that went well recently that you feel proud of?' }],
        [
          {
            text: 'What’s a moment where you handled something better than you would have a year ago?',
          },
        ],
        [{ text: 'What’s a challenge you navigated well recently?' }],
      ),
      p(),
      p({ text: 'Unpack the success', bold: true }),
      ul(
        [{ text: 'What did you do that made that outcome possible?' }],
        [{ text: 'What strengths did you rely on?' }],
        [{ text: 'If we replayed that moment, what did you do well?' }],
      ),
      p(),
      p({ text: 'Extract the lesson', bold: true }),
      ul(
        [{ text: 'What does that success tell you about yourself?' }],
        [{ text: 'Where else could you apply that same approach?' }],
        [{ text: 'What lesson do you want to carry forward?' }],
      ),
      p(),
      p({ text: '2. Review - Check progress on previous commitments', bold: true }),
      p(),
      p({ text: 'Start the check-in', bold: true }),
      ul(
        [
          { text: 'Last time we talked about ' },
          { text: '[action step]', bold: true },
          { text: '. How did that go?' },
        ],
        [{ text: 'What progress did you make since our last conversation?' }],
      ),
      p(),
      p({ text: 'If progress happened', bold: true }),
      ul(
        [{ text: 'What helped you follow through?' }],
        [{ text: 'What part was easier than expected?' }],
        [{ text: 'What did you learn?' }],
      ),
      p(),
      p({ text: 'If progress stalled', bold: true }),
      ul(
        [{ text: 'What got in the way?' }],
        [{ text: 'Looking back, what might have helped?' }],
        [{ text: 'What would make this easier to execute moving forward?' }],
      ),
      p(),
      p({ text: '3. Diagnose - Surface the student’s current challenge', bold: true }),
      p(),
      p({ text: 'Open the floor', bold: true }),
      ul(
        [{ text: 'What feels most important for us to talk about today?' }],
        [{ text: 'What’s been most on your mind since we last met?' }],
        [{ text: 'If we could make progress on one thing today, what would it be?' }],
      ),
      p(),
      p({ text: 'Clarify the situation', bold: true }),
      ul(
        [{ text: 'What exactly is happening right now?' }],
        [{ text: 'What part feels most confusing?' }],
        [{ text: 'What decision are you trying to make?' }],
      ),
      p(),
      p({ text: 'Surface assumptions', bold: true }),
      ul(
        [{ text: 'What assumptions might you be making?' }],
        [{ text: 'What information might still be missing?' }],
      ),
      p(),
      p({ text: 'Find the constraint', bold: true }),
      ul(
        [{ text: 'What’s making this difficult right now?' }],
        [{ text: 'What do you think is the hardest part?' }],
      ),
      p(),
      p({ text: '4. Resolve - Help the student think through the challenge', bold: true }),
      p(),
      p({ text: 'Explore options', bold: true }),
      ul(
        [{ text: 'What options are you considering?' }],
        [{ text: 'What matters most to you in this decision?' }],
      ),
      p(),
      p({ text: 'Think through consequences', bold: true }),
      ul(
        [{ text: 'What might each option lead to over the next few years?' }],
        [{ text: 'What could be the upside?' }],
        [{ text: 'What risks or downsides should you consider?' }],
      ),
      p(),
      p({ text: 'Encourage perspective', bold: true }),
      ul(
        [{ text: 'If you were advising a friend, what would you say?' }],
        [{ text: 'Which option seems most aligned with the life you want to build?' }],
        [{ text: 'Which option would you regret not exploring?' }],
      ),
      p(),
      p({ text: '5. Focus - Identify the key lever', bold: true }),
      p({ text: 'Coach frames the recommendation.' }),
      p(
        {
          text: '“Based on where you are right now, the most important thing to focus on over the next ',
        },
        { text: '[month / quarter]', bold: true },
        { text: ' is ' },
        { text: '[priority]', bold: true },
        { text: '.”' },
      ),
      p(),
      p({ text: 'Check alignment', bold: true }),
      ul(
        [{ text: 'How does that focus area sound to you?' }],
        [{ text: 'Does that feel like the right place to concentrate your energy?' }],
        [{ text: 'What part of that feels most motivating to you?' }],
      ),
      p(),
      p({ text: '6. Follow Through - Lock in next steps', bold: true }),
      ul(
        [{ text: 'What is the most important step you’ll take before our next session?' }],
        [{ text: 'When ' }, { text: 'specifically' }, { text: ' will you do that?' }],
        [{ text: 'What might get in the way?' }],
      ),
      p(),
      p({ text: '7. Confirm:', bold: true }),
      ul(
        [{ text: 'clear action step' }],
        [{ text: 'timeframe' }],
        [{ text: 'relevant Backrs tools/resources' }],
        [{ text: 'lock in next month’s meeting ' }],
      ),
    ]),
  },
  {
    id: 'blueprint-advising-session',
    label: 'Blueprint Advising Session',
    body: buildBody([
      p({ text: 'BLUEPRINT 1:1 ADVISING SESSION', bold: true }),
      p(),
      p({ text: 'Recommended Time:', bold: true }, { text: ' 20–30 minutes' }),
      p({ text: 'Format:', bold: true }, { text: ' In person or virtual' }),
      p(
        { text: 'Prerequisite:', bold: true },
        { text: ' Student has completed Blueprint before the meeting' },
      ),
      p(),
      p(
        { text: 'SESSION ESSENTIAL QUESTION:', bold: true },
        {
          text: ' How do career tracks align with my life aspirations, and what educational investments should I consider and pursue in order to achieve my target career track?',
        },
      ),
      p(),
      p(
        { text: 'SESSION ESSENTIAL UNDERSTANDING:', bold: true },
        {
          text: ' When I genuinely understand the life I desire to live, I can explore and identify career paths and educational investments that will allow me to achieve my goals.',
        },
      ),
      p(),
      p(
        { text: 'PURPOSE:', bold: true },
        {
          text: ' The purpose of this conversation is to help the student engage with their completed Blueprint results and better understand their life aspirations, target career track, range of potential career paths, educational investments, and immediate next steps.',
        },
      ),
      p(),
      p({
        text: 'Blueprint is not intended to tell a student what they should do. It should help them better understand the future they currently want, explore multiple ways of getting there, and make choices now that preserve strong options later.',
      }),
      p(),
      p({
        text: 'Students should revisit Blueprint over time as they gain new information, experience, and self-knowledge.',
      }),
      p(),
      p({ text: 'ADVISOR SETUP', bold: true }),
      p(),
      p(
        { text: 'In person:', bold: true },
        {
          text: ' Have the student open Blueprint on their own device. Sit so you can review the results together, but let the student control the screen.',
        },
      ),
      p(),
      p(
        { text: 'Virtual:', bold: true },
        {
          text: ' Ask the student to open Blueprint and share their screen. The student should navigate through their own results. Alternatively, you can pull up their Blueprint on your coaching dashboard.',
        },
      ),
      p(),
      p({
        text: 'In either format, the student should do most of the interpreting and talking. Use the results as prompts for conversation rather than explaining every section to them.',
      }),
      p(),
      p({ text: 'AGENDA', bold: true }),
      p(),
      p({ text: '1. Reconnect to the Purpose: 2 minutes' }),
      p({ text: '2. Life Aspirations: 4–5 minutes' }),
      p({ text: '3. Career Track: 5 minutes' }),
      p({ text: '4. Career Path Exploration: 5–7 minutes' }),
      p({ text: '5. Educational Investments: 4–5 minutes' }),
      p({ text: '6. Commitments and Closing: 2–4 minutes' }),
      p(),
      p({ text: 'Advisor:', bold: true }),
      p({
        text: '“You’ve already completed your Blueprint, so today we’re not going to retake it. We’re going to use your results to think more deeply about what you want to be true for your life and what that might mean for some of the choices you make now.”',
      }),
      p(),
      p({
        text: '“None of this is meant to tell you exactly what career you need to pursue. You are going to learn more about yourself and the world, and your answers will probably change. The point is to understand what you want right now, what that future might require, and how you can keep strong options open.”',
      }),
      p(),
      p(
        { text: 'Opening reflection:', bold: true },
        {
          text: ' “Before we even look at the results, what do you remember thinking about most while you were completing Blueprint?”',
        },
      ),
      p(),
      p(
        { text: 'LIFE ASPIRATIONS:', bold: true },
        { text: ' Have the student open Summary of Life Aspirations.' },
      ),
      p(),
      p({ text: 'Advisor:', bold: true }),
      p({
        text: '“Let’s first start by reviewing your aspirations. This section should reflect what you said you want to be true about your life in your 30s, 40s, and beyond.”',
      }),
      p(),
      p({
        text: '“Take a minute and read through it again. Don’t worry about whether these are the ‘right’ answers. Think about how true they actually feel for you.”',
      }),
      p(),
      p({ text: 'Pause and reflect. Choose two or three questions:', bold: true }),
      ul(
        [{ text: '“What here feels most true for you?”' }],
        [
          {
            text: '“Is there anything here that doesn’t seem quite right? What would make it more true?”',
          },
        ],
        [{ text: '“What result surprised you?”' }],
        [{ text: '“Which of these aspirations matters most to you right now? Why?”' }],
      ),
      p(),
      p({ text: 'Advisor:', bold: true }),
      p({
        text: '“Being clear on your aspirations can help inform all sorts of decisions, from the classes you take to your postsecondary pathway, extracurriculars, jobs, internships, and eventually career decisions.”',
      }),
      p(),
      p(
        { text: 'CAREER TRACK:', bold: true },
        { text: ' Navigate to Minimum Career Track / Target Track.' },
      ),
      p(),
      p({ text: 'Advisor:', bold: true }),
      p({
        text: '“Now let’s look at your career track. Don’t think of a track as a specific job. Think of it as the range and level of professional opportunities you are preparing yourself to access.”',
      }),
      p(),
      p(
        {
          text: '“You may change career paths several times in your life. That’s normal. What matters right now is understanding the level of preparation your aspirations may require and making choices that keep strong options available to you. ',
        },
        {
          text: '[Insert metaphor to help student understand the distinction between tracks and paths - e.g. corporate attorney vs. family attorney, division 1 athlete vs. division 3 athlete.]',
          bold: true,
        },
        { text: '”' },
      ),
      p(),
      p({ text: 'Ask the student to read their Career Track description.' }),
      p(),
      p({ text: 'Pause and reflect. Choose two to three questions:', bold: true }),
      ul(
        [{ text: '“What about this track feels consistent with the life you described?”' }],
        [{ text: '“What feels challenging about it?”' }],
        [{ text: '“Are there any tradeoffs here that you hadn’t thought about before?”' }],
        [{ text: '“Does this feel like the right target for you right now? Why or why not?”' }],
      ),
      p(),
      p({
        text: 'If the student disagrees with the result, explore the disagreement rather than convincing them the result is correct. Consider asking:',
      }),
      ul(
        [{ text: '“What do you think Blueprint may be picking up on that led to this result?”' }],
        [
          {
            text: '“What would need to change about your life aspirations for a different track to make more sense?”',
          },
        ],
      ),
      p(),
      p({ text: 'Connect the future to now:', bold: true }),
      p({
        text: '“If this is the track you want to preserve access to, what do you think you need to be especially intentional about over the next year?”',
      }),
      p(),
      p({
        text: 'If helpful, use Discuss → “How do I get on this track?” and review Lubav’s suggestions together.',
      }),
      p(),
      p({ text: '“Which of these suggestions feels most important for you right now?”' }),
      p(),
      p(
        { text: 'CAREER PATH EXPLORATION:', bold: true },
        { text: ' Navigate to Potential Career Paths.' },
      ),
      p(),
      p({ text: 'Advisor:', bold: true }),
      p({
        text: '“Here’s one of the most important ideas in Blueprint: there are probably many career paths that could be a strong fit for you.”',
      }),
      p(),
      p({
        text: '“You do not need to decide exactly what job you want right now. In fact, we want you exploring. The goal is to make choices now that give you many strong options later.”',
      }),
      p(),
      p({ text: 'Review the Strong Fit Career Paths together.' }),
      p(),
      p({ text: 'Pause and reflect:', bold: true }),
      ul(
        [{ text: '“Which of these immediately catches your attention?”' }],
        [{ text: '“Which one surprises you?”' }],
      ),
      p(),
      p({
        text: 'Have the student select one career path to explore more deeply during the meeting.',
      }),
      p(),
      p({ text: 'Ask the student to read the explanation of why the path may be a good fit.' }),
      ul(
        [{ text: '“What about this sounds appealing to you?”' }],
        [{ text: '“What does not sound appealing?”' }],
        [
          {
            text: '“What would you want to experience or learn before deciding whether this is actually a good fit?”',
          },
        ],
      ),
      p(),
      p({ text: 'If time permits, briefly review:' }),
      ul(
        [{ text: 'Example roles' }],
        [{ text: 'Earnings by career track' }],
        [{ text: 'Top majors' }],
        [{ text: 'How AI may affect the field' }],
        [{ text: 'Internships and other opportunities' }],
      ),
      p(),
      p({
        text: 'Then ask: “What is one other path you want to explore after our conversation today?”',
      }),
      p(),
      p({
        text: 'The student should leave with multiple possibilities to explore, not pressure to select a single career.',
      }),
      p(),
      p(
        { text: 'EDUCATIONAL INVESTMENTS:', bold: true },
        { text: ' Navigate to Educational Investments.' },
      ),
      p(),
      p({ text: 'Advisor:', bold: true }),
      p({
        text: '“Let’s move to the final part of your Blueprint. Based on the life you defined for yourself and your target career track, Blueprint identifies educational investments that may help you reach that future.”',
      }),
      p(),
      p({
        text: '“Think of educational investments as choices you make with your time, money, effort, and resources in order to create stronger future opportunities.”',
      }),
      p(),
      p({ text: 'Review the recommended investments.' }),
      p(),
      p({ text: 'Pause and reflect:', bold: true }),
      ul(
        [{ text: '“Which of these makes the most sense to you?”' }],
        [{ text: '“Which one feels hardest or least clear?”' }],
      ),
      p(),
      p({ text: 'Then move from understanding to prioritization:', bold: true }),
      p({
        text: '“Of everything we’ve looked at today, which educational investment matters most for you to focus on next?”',
      }),
      p(),
      p({
        text: 'For a high school student, this might relate to coursework, academic preparation, postsecondary exploration, experiences, or applications.',
      }),
      p(),
      p({
        text: 'For a college student, it might relate to major or program decisions, academic performance, internships, skill development, networking, postgraduate education, or career preparation.',
      }),
      p(),
      p({
        text: 'Do not try to solve every educational investment during this meeting. Prioritize what matters most now.',
      }),
      p(),
      p(
        { text: 'COMMITMENT AND CLOSING:', bold: true },
        { text: ' Bring the conversation back to the full Blueprint.' },
      ),
      p(),
      p({ text: 'Ask:', bold: true }),
      ul(
        [
          {
            text: '“What is the most important thing you learned about yourself from reviewing your Blueprint today?”',
          },
        ],
        [
          {
            text: '“What is one thing you want to do differently or learn more about because of what you saw?”',
          },
        ],
      ),
      p(),
      p({ text: 'Help the student turn that answer into a specific next step.' }),
      p(),
      p({ text: 'Possible next steps include:' }),
      ul(
        [{ text: 'Research two career paths.' }],
        [{ text: 'Speak with someone working in a field of interest.' }],
        [{ text: 'Explore what preparation is required for a target college or program.' }],
        [{ text: 'Investigate an internship or work-based learning experience.' }],
        [{ text: 'Review upcoming course choices.' }],
        [{ text: 'Learn more about a recommended educational investment.' }],
        [{ text: 'Ask an advisor about an academic or career-track gap.' }],
      ),
      p(),
      p({ text: 'Then ask: “What will you do, and by when?”' }),
      p(),
      p({ text: 'If appropriate: “Who else should know what you’re thinking about right now?”' }),
      p(),
      p({
        text: 'Encourage the student to share their Blueprint with a counselor, parent or guardian, mentor, professor, teacher, coach, or another person invested in their success.',
      }),
      p(),
      p({ text: 'CLOSING SCRIPT', bold: true }),
      p(),
      p({
        text: '“As we close out, I want to reiterate that this is not a one-and-done experience. Going through Blueprint now doesn’t mean this is what you have to want forever.”',
      }),
      p(),
      p({
        text: '“The point is to help you understand what you want to be true right now, explore what might be available to you, and make choices that keep strong opportunities open.”',
      }),
      p(),
      p({
        text: '“As you grow, learn more about the world, and gain new experiences, your thinking may change. We’ll keep coming back to your Blueprint and using it to ask whether the choices you’re making are helping you move toward the life you want.”',
      }),
    ]),
  },
  {
    id: 'are-you-on-track-advising-session',
    label: 'Are You On Track Advising Session',
    body: buildBody([
      p({ text: 'Session Overview', bold: true }),
      p(),
      p(
        { text: 'Essential Question:', bold: true },
        {
          text: ' Where do I stand in relation to the future I’m working toward, and what should I focus on next?',
        },
      ),
      p(),
      p(
        { text: 'Essential Understanding:', bold: true },
        {
          text: ' Understanding my current standing relative to my aspirations helps me identify the investments that will have the greatest impact on my future opportunities. Progress requires choosing the right priorities, taking intentional action, and regularly assessing results.',
        },
      ),
      p(),
      p({ text: 'Objectives', bold: true }),
      p({ text: 'By the end of this session, students will be able to:' }),
      p(
        { text: '1. ' },
        { text: 'Interpret their current standing', bold: true },
        { text: ' relative to their target career track and life aspirations.' },
      ),
      p(
        { text: '2. ' },
        { text: 'Identify strengths and opportunities for growth', bold: true },
        {
          text: ' using evidence from their AYOT Scorecard and the Standards by Track across the Four Enduring Unlocks.',
        },
      ),
      p(
        { text: '3. ' },
        { text: 'Identify one Key Lever', bold: true },
        { text: ' that represents a high-impact opportunity to improve their future readiness.' },
      ),
      p(
        { text: '4. ' },
        { text: 'Develop an action plan', bold: true },
        {
          text: ' with 2–3 specific next steps, relevant Backrs resources, and a timeline for follow-up.',
        },
      ),
      p(),
      p({ text: 'Frame: Connect to Aspirations', bold: true }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Before we look at where you stand today, let’s start with where you’re trying to go. Think back to your Blueprint. You identified what you want your life to look like in your 30s, 40s, and beyond. You thought about your career, finances, lifestyle, and the opportunities you want available to you.',
      }),
      p(),
      p({
        text: 'This process also helped identify your target career track. Remember, a track isn’t a specific job or career path. It’s the general level of professional opportunity, earning potential, and growth you’re preparing to access.',
      }),
      p(),
      p({
        text: 'Today, we’re going to use Are You On Track to understand where you are right now and unpack what you’re doing well and where you can focus in the coming months. Importantly, we’re not comparing you to other students. We’re comparing where you stand today with the aspirations you’ve set for yourself.',
      }),
      p(),
      p({
        text: 'Your results aren’t permanent, and they don’t determine what you’re capable of achieving. They give us a clearer picture of where you are, what’s going well, and where you may need to invest more effort.',
      }),
      p(),
      p({
        text: 'By the end of today, you’ll know where to focus and have a specific plan for moving forward.”',
      }),
      p(),
      p({ text: 'Student Action', bold: true }),
      p({ text: 'Review your target career track in Blueprint.' }),
      p(),
      p(
        { text: 'Ask:', bold: true },
        { text: ' “Does this still reflect the future you’re working toward?”' },
      ),
      p(),
      p({ text: 'Assess: Understand Your Current Standing', bold: true }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Great. Now let’s open Are You On Track. Before we interpret your results, let’s make sure your information is accurate. Let’s quickly review your transcript, GPA, coursework, test scores if applicable, and resume. Your assessment is only as useful as the information behind it.”',
      }),
      p(),
      p(
        { text: 'Note:', bold: true },
        { text: ' Advisor will likely need to confirm rigor and unweighted GPA.' },
      ),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Let’s start with the graph at the top. The dark line represents your current academic track. The light blue section represents the target track you identified through Blueprint. The circle around your profile picture reflects the strength of your non-academic profile, which includes your experiences, achievements, skills, and communities. The dotted lines indicate your potential. Of course, nothing is set in stone.',
      }),
      p(),
      p({
        text: 'Your academic profile helps us understand the opportunities you’re academically preparing to access. Your non-academic profile helps us understand the experiences, capabilities, and relationships you’re building to pursue and succeed in those opportunities. Both are important.',
      }),
      p(),
      p(
        { text: 'So what this is saying is that, academically, you ' },
        { text: '[are / are not]', bold: true },
        { text: ' currently on track to your stated aspirations.”' },
      ),
      p(),
      p({ text: '“What stands out or surprises you about your results?”' }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“If your current academic track meets or exceeds your target, that’s encouraging. We want to understand what’s driving that result and what you need to sustain or strengthen.',
      }),
      p(),
      p({
        text: 'If your current academic track is below your target, that tells us there’s a gap worth investigating.',
      }),
      p(),
      p({
        text: 'In either case, we want to understand what’s behind your results so we can make informed decisions about what to do next.”',
      }),
      p(),
      p(
        { text: 'Optional:', bold: true },
        { text: ' Select “Explain This to Me” to review the interpretation of the results.' },
      ),
      p(),
      p({ text: 'Diagnose: Identify Strengths & Opportunities', bold: true }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“The graph gives us the big picture. Now we need to understand what’s driving your results.”',
      }),
      p(),
      p({
        text: '“Your Scorecard evaluates 20 dimensions across the Four Enduring Unlocks. These are the building blocks of long-term success. Let me quickly define these:',
      }),
      p(
        { text: '1. ' },
        { text: 'Academic Foundations', bold: true },
        {
          text: ' - Your academic performance, coursework, and preparation for future educational and career opportunities.',
        },
      ),
      p(
        { text: '2. ' },
        { text: 'Real-World Experience & Achievement', bold: true },
        {
          text: ' - Your experiences through work, internships, leadership, projects, and other activities that demonstrate what you can do.',
        },
      ),
      p(
        { text: '3. ' },
        { text: 'Mindsets & Skills for a Post-AI Economy', bold: true },
        {
          text: ' - The habits, mindsets, and skills that help you take ownership, navigate challenges, and succeed in an evolving world.',
        },
      ),
      p(
        { text: '4. ' },
        { text: 'Engagement in Communities of Success', bold: true },
        {
          text: ' - The relationships and communities that support your development and connect you to new opportunities.',
        },
      ),
      p(),
      p({ text: 'Any questions on these?”' }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({ text: '“Now, how do we know what strong preparation actually looks like?' }),
      p(),
      p({
        text: 'Click the information (i) icon next to an Unlock. This opens the Standards by Track.',
      }),
      p(),
      p({
        text: 'These standards draw on research and data about educational preparation and career outcomes. They provide markers that help us understand what students should be building for different career tracks.',
      }),
      p(),
      p({
        text: 'For example, we can compare your GPA and coursework to the academic standards for your target track. We can also examine your experiences, skills, and relationships.',
      }),
      p(),
      p({
        text: 'The goal is to understand where you’re strong and where additional investment might make the biggest difference.”',
      }),
      p(),
      p({
        text: '“Now let’s take a minute to identify a couple strengths of yours across the unlocks. We can identify these by seeing where you currently stand on the rubrics or by clicking ‘Strengths’ at the bottom of each column.”',
      }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Nicely done. You should be proud of these accomplishments and the work you’ve put in to get there. Now you can’t let up in these areas. You’ll want to continue leaning in on these strengths.”',
      }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“As you’re reviewing your results, you might notice some conflicting signals. You could have strong academics but limited real-world experience. You could have impressive work or leadership experience but an academic profile below your target track. Or the assessment might be missing important information.',
      }),
      p(),
      p({
        text: 'That’s why we need to look at the evidence behind the ratings. If something doesn’t seem right, click into that dimension and review the explanation. Check whether the information is accurate and complete. You can adjust a rating when you have evidence to support a change, but you’ll need to explain why. We want the most accurate picture possible, even if it tells us something we weren’t expecting.”',
      }),
      p(),
      p({ text: 'Student Reflection', bold: true }),
      p({
        text: 'Of the strengths you identified, which are you most proud of? Of the opportunities, which could have the greatest impact on your future?',
        bold: true,
      }),
      p(),
      p({ text: 'Prioritize: Identify Your Key Lever', bold: true }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Now we have a much clearer picture of where you’re strong and where you have opportunities to grow. But here’s what’s important: not every investment in your future will have the same impact, and timing matters.',
      }),
      p(),
      p({
        text: 'For example, improving your GPA during high school may significantly expand your postsecondary options. Building meaningful career experience during college may have a greater impact on your ability to launch a strong career.',
      }),
      p(),
      p({
        text: 'There are lots of things you could work on. Our goal is to identify the 1-2 key levers that could make the biggest difference right now.”',
      }),
      p(),
      p({ text: 'Advisor Guidance', bold: true }),
      p({ text: 'Use three sources to identify the highest-leverage priority:' }),
      p(
        { text: '1. ' },
        { text: 'Backrs Platform:', bold: true },
        {
          text: ' The student’s Blueprint, AYOT results, Scorecard, Standards, and supporting evidence.',
        },
      ),
      p(
        { text: '2. ' },
        { text: 'Coaching Key Levers:', bold: true },
        {
          text: ' The grade- or year-specific recommendations, including Primary, Secondary, Tertiary, and Conditional priorities.',
        },
      ),
      p(
        { text: '3. ' },
        { text: 'Counselor Judgment:', bold: true },
        {
          text: ' What you know about the student’s aspirations, circumstances, motivation, barriers, and current opportunities.',
        },
      ),
      p(),
      p({
        text: 'The Key Levers provide recommended starting points. They should inform, rather than replace, an individualized decision.',
      }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p(
        {
          text: '“Let’s look at the opportunities you’ve identified. Based on your results, your aspirations, and where you are in your educational journey, the most important area for you to focus on is ',
        },
        { text: '[Key Lever]', bold: true },
        { text: '.' },
      ),
      p(),
      p(
        { text: 'Here’s why: ' },
        {
          text: '[Explain the evidence, the connection to the student’s goals, and why the timing is important.]',
          bold: true,
        },
      ),
      p(),
      p({ text: 'What do you think? Does this feel like the right place to focus?”' }),
      p(),
      p({
        text: '“We’re going to build your plan around this priority. That doesn’t mean the other areas aren’t important. It means we’re being intentional about where you invest your time and effort over the next few months.”',
      }),
      p(),
      p({ text: 'Execute: Build Your Action Plan', bold: true }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Identifying your Key Lever is an important first step. Now we need to turn it into action. Backrs has tools, resources, and opportunities to help you make progress in each of these areas. The goal is to leave today with two or three specific actions you can take over the next few months. These should be things you can actually do and that will move you closer to your goals.”',
      }),
      p(),
      p(
        { text: 'HS Example:', bold: true },
        {
          text: ' “Let’s say you’re an 11th grader and your Key Lever is improving your GPA. A vague goal would be ‘do better in school.’ A stronger plan might be to identify the two classes having the biggest impact on your GPA, attend tutoring twice a week, and establish a weekly study schedule.”',
        },
      ),
      p(),
      p(
        { text: 'College Example:', bold: true },
        {
          text: ' “Imagine you’re a college student who needs to strengthen your career exploration and professional network. Your plan might be to identify five professionals in fields you’re interested in, conduct five career interviews, and use what you learn to identify relevant internship opportunities.”',
        },
      ),
      p(),
      p({
        text: '“The point is to turn a broad goal into actions that are specific, realistic, and likely to make a difference.”',
      }),
      p(),
      p({
        text: '“Let’s identify the Tools, Guides, Playbooks, and resources in Backrs that can support your key lever.”',
      }),
      p(),
      p(
        { text: '“To support this key lever, your next steps are: ' },
        {
          text: '[enumerate next steps, share relevant resources, and assign deadlines for each task]',
          bold: true,
        },
        { text: '”' },
      ),
      p(),
      p({ text: 'Commit: Establish Your Next Check-In', bold: true }),
      p(),
      p({ text: 'Advisor Script', bold: true }),
      p({
        text: '“Let’s return to where we started. You revisited the future you’re working toward and used AYOT to understand where you currently stand. You looked at your strengths, identified opportunities for growth, and selected the Key Lever that deserves your attention right now. Most importantly, you’ve developed a plan to make progress. But this isn’t something we do once and forget about. As you improve academically, develop skills, gain experiences, and build relationships, your profile will change. We need to regularly assess your progress and adjust your priorities as you grow.”',
      }),
      p(),
      p({
        text: '“Before we finish, let’s discuss next steps. You’ll begin working on your action steps this week. At our next check-in, we’ll review what you’ve accomplished, what’s working, and where you might need to adjust. You’ll also continue updating AYOT as you build new evidence, ideally each quarter or semester. Our goal is to keep making informed investments in your future, one step at a time. Appreciate your reflection and commitment today.”',
      }),
      p(),
      p({ text: 'Evidence of Success', bold: true }),
      p({
        text: 'A successful AYOT session should result in every student being able to answer five questions:',
      }),
      p(
        { text: '1. ' },
        { text: 'Where am I headed?', bold: true },
        { text: ' I understand my target career track and how it relates to my aspirations.' },
      ),
      p(
        { text: '2. ' },
        { text: 'Where am I now?', bold: true },
        { text: ' I understand my current academic and non-academic profile.' },
      ),
      p(
        { text: '3. ' },
        { text: 'What’s driving my results?', bold: true },
        {
          text: ' I can identify specific strengths and gaps using evidence and the Standards by Track.',
        },
      ),
      p(
        { text: '4. ' },
        { text: 'What matters most?', bold: true },
        { text: ' I’ve identified one Key Lever that can meaningfully improve my trajectory.' },
      ),
      p(
        { text: '5. ' },
        { text: 'What will I do next?', bold: true },
        {
          text: ' I have 2–3 specific actions, relevant supports, and a timeline for making progress.',
        },
      ),
    ]),
  },
];
